import { Badge, Button, IconButton, Tooltip, useTheme } from '@material-ui/core'
import React from 'react'
import { Attachment } from '../../Message/Attachment'
import { Container } from '../../Container'
import { WorkorderStatusBadge } from '../../WorkorderStatusBadge'
import { WorkorderTransitionButtons } from '../../WorkorderStatusTransitionButtons'
import {
    AddAPhoto,
    ChatBubbleOutline,
    ReportProblem,
    ExitToApp,
    AssignmentIndOutlined,
    Edit,
    PlayCircleOutline,
} from '@material-ui/icons'
import DoneAll from '@material-ui/icons/DoneAll'
import FormatPaintIcon from '@material-ui/icons/FormatPaint'
import {
    BaseWorkorder,
    EarlyAccessRequest,
    User,
    WorkSpaceUser,
    WorkorderStatus,
    WorkorderType,
    getServiceImageDetails,
} from '../../../models'
import {
    MessageDetailActionThunk,
    TransitionWorkorderParams,
    WorkorderResponse,
} from '../../../store'

import { ReactComponent as UnitNotesIcon } from '../../../assets/icons/unitnotes.svg'
import { ReactComponent as ActionItemIcon } from '../../../assets/icons/action-item.svg'
import { mix_hexes, toMMDDYY, VENDOR } from '../../../helpers'
import { toast } from 'react-toastify'
import { useStyles } from '../../../styles'
import { WorkorderAreaCard } from './WorkorderAreaCard'
import { WorkorderDateCard } from './WorkorderDateCard'
import { WorkorderInfoPopover } from '../WorkorderInfoPopover'
import { EarlyAccessRequestTransitionButtons } from '../../EarlyAccessRequestTransitionButtons'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    workorder: WorkorderResponse
    isSelectedWorkorder: boolean
    user?: WorkSpaceUser
    style?: React.CSSProperties
    hideMessageButtons?: boolean
    hideCameraButton?: boolean
    canSeeUnitNotes: boolean
    selectionMode?: boolean
    width?: number | string
    disabled?: boolean
    hideDates?: boolean
    showResetButton?: boolean
    earlyAccessRequest?: EarlyAccessRequest
    openMessageDrawer: () => void
    openUnitNotes: () => void
    transitionWorkorder: (
        workorder: BaseWorkorder,
        status: WorkorderStatus,
        params?: TransitionWorkorderParams,
    ) => Promise<void>
    onClickWorkorder: (wo: WorkorderResponse) => void
    updateWorkorderState: (wo: WorkorderResponse) => void
    createMessage?: (req: FormData) => MessageDetailActionThunk
    navigateToSchedule?: (scheduleId: number) => void
    getRFPDetail?: () => void
    openEditModal?: (workorder: WorkorderResponse) => void
    openServiceRequestChannel?: (workorder: WorkorderResponse) => void
    onCreateWorkorderEarlyAccessRequest?: (workorder: WorkorderResponse) => void
    onHandleEarlyAccessRequest?: (approve: boolean) => void
    onClickGoBack?: () => void
    onClickVideo?: (videoId: number) => void
}

export const WorkorderRow = (props: Props) => {
    const {
        workorder,
        user,
        isSelectedWorkorder,
        hideMessageButtons,
        hideCameraButton,
        selectionMode,
        width,
        disabled,
        hideDates,
        showResetButton,
        earlyAccessRequest,
        openMessageDrawer,
        openUnitNotes,
        transitionWorkorder,
        onClickWorkorder,
        updateWorkorderState,
        createMessage,
        navigateToSchedule,
        getRFPDetail,
        openEditModal,
        openServiceRequestChannel,
        onCreateWorkorderEarlyAccessRequest,
        onHandleEarlyAccessRequest,
        onClickGoBack,
        onClickVideo,
    } = props

    const theme = useTheme()
    const classes = useStyles()

    let attachment = null
    if (workorder.attachment_list[0]) {
        attachment = workorder.attachment_list[0]
    }

    const priorityIcon =
        (workorder as any).priority === 1 ? (
            <Tooltip title="Priority">
                <ReportProblem color="secondary" fontSize="small" />
            </Tooltip>
        ) : null

    let titleLabel = ''
    if (
        workorder.schedule_name !== null &&
        workorder.schedule_name !== undefined &&
        workorder.title !== ''
    ) {
        titleLabel = `${workorder.schedule_name} / ${workorder.title}`
    } else {
        titleLabel = workorder.schedule_name ?? workorder.title
    }

    let navigationIcon: JSX.Element | null = null
    if (workorder.schedule_id !== null && navigateToSchedule) {
        navigationIcon = (
            <Tooltip title={`Go to ${workorder.schedule_name}`}>
                <IconButton
                    style={{
                        border: `1px solid ${theme.palette.grey[300]}`,
                        marginRight: theme.spacing(1),
                    }}
                    onClick={() => {
                        navigateToSchedule(workorder.schedule_id!)
                    }}
                >
                    <ExitToApp fontSize="small" />
                </IconButton>
            </Tooltip>
        )
    }

    let backgroundColor = ''
    if (isSelectedWorkorder) {
        backgroundColor = theme.palette.grey[200]
    }
    if (isSelectedWorkorder && selectionMode) {
        backgroundColor = mix_hexes(theme.palette.secondary.main, '#ffffff')
    }

    let workorderIcon = <FormatPaintIcon />
    let workorderTooltip = 'Service Order'
    if (workorder.type === WorkorderType.ACTION_ITEM) {
        workorderIcon = (
            <ActionItemIcon
                style={{
                    height: 25,
                    width: 25,
                }}
            />
        )
        workorderTooltip = 'Action Item'
    } else if (workorder.type === WorkorderType.SERVICE_REQUEST) {
        workorderIcon = (
            <IconButton
                onClick={(e) => {
                    e.stopPropagation()
                    openServiceRequestChannel?.(workorder)
                }}
            >
                <Badge
                    badgeContent={workorder?.service_request?.message_count}
                    color={
                        (workorder?.service_request?.unread_count ?? 0) > 0
                            ? 'secondary'
                            : 'primary'
                    }
                >
                    <AssignmentIndOutlined />
                </Badge>
            </IconButton>
        )
        workorderTooltip = 'Service Request'
    }

    let editButton: JSX.Element | null = null
    if (!workorder.price_locked && openEditModal) {
        editButton = (
            <Tooltip title="Edit">
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        openEditModal(workorder)
                    }}
                >
                    <Edit />
                </IconButton>
            </Tooltip>
        )
    }

    let cantStartWorkorder = disabled
    if (workorder.approved_early_access_request_id !== null) {
        cantStartWorkorder = false
    }

    let showUnitName = true
    if (user?.active_workspace?.company_type === VENDOR && cantStartWorkorder) {
        showUnitName = false
    }

    return (
        <Container
            style={{
                border: `1px solid ${theme.palette.grey[300]}`,
                width: width ? width : '600px',
                marginBottom: theme.spacing(2),
                cursor: 'pointer',
                backgroundColor: backgroundColor,
                borderRadius: theme.shape.borderRadius,
                position: 'relative',
                ...props.style,
            }}
            direction="column"
            onClick={
                selectionMode ? undefined : () => onClickWorkorder(workorder)
            }
            className={classes.hoverGrey100}
        >
            <Container
                style={{
                    height: '160px',
                    borderBottom: `1px solid ${theme.palette.grey[300]}`,
                }}
            >
                {/* Left Column title, service, vendor & location */}
                <Container
                    style={{
                        flex: 1,
                        flexDirection: 'column',
                        padding: theme.spacing(1),
                    }}
                >
                    {/* Title */}
                    <Container style={{ alignItems: 'center' }}>
                        {navigationIcon}
                        <span
                            style={{
                                ...typographyStyle(theme.typography.subtitle1),
                            }}
                        >
                            {titleLabel}
                        </span>
                    </Container>

                    <Container>
                        {/* Location */}
                        <Container
                            style={{
                                flexDirection: 'column',
                                maxWidth: 150,
                                minWidth: 150,
                                overflow: 'hidden',
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    fontWeight: 700,
                                }}
                            >
                                {workorder.path}
                                {workorder.folder_name}
                            </span>

                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h6),
                                    color: theme.palette.primary.dark,
                                }}
                            >
                                {showUnitName ? workorder.unit_name ?? '' : ''}
                            </span>

                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    fontWeight: 700,
                                }}
                            >
                                {workorder.unit_config_name ?? ''}
                            </span>
                        </Container>

                        {/* Service & vendor */}
                        <Container
                            style={{
                                flexDirection: 'column',
                                flex: 1,
                                overflow: 'hidden',
                                marginLeft: theme.spacing(1),
                            }}
                        >
                            <Container style={{ overflow: 'hidden' }}>
                                <img
                                    src={
                                        getServiceImageDetails(
                                            workorder.service_id ?? -1,
                                            workorder.service_category,
                                        ).icon
                                    }
                                    style={{
                                        maxHeight: 25,
                                        maxWidth: 27,
                                        marginRight: theme.spacing(1),
                                    }}
                                />
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.body2,
                                        ),
                                        fontWeight: 600,
                                    }}
                                >
                                    {workorder.service_name ?? 'No Service'}
                                </span>
                            </Container>

                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    fontWeight: 600,
                                }}
                            >
                                {workorder.vendor_name ??
                                    workorder.user_name ??
                                    'No Vendor'}
                            </span>
                        </Container>
                    </Container>
                </Container>

                {/* Middle column service areas & date */}
                <Container flex={1} direction="column">
                    <WorkorderAreaCard workorder={workorder} theme={theme} />
                    {!hideDates ? (
                        <WorkorderDateCard
                            workorder={workorder}
                            theme={theme}
                        />
                    ) : (
                        <Container
                            style={{
                                flex: 1,
                                ...typographyStyle(theme.typography.body2),
                                borderRadius: theme.shape.borderRadius,
                                border: `1px solid ${theme.palette.grey[300]}`,
                                padding: theme.spacing(1),
                                boxSizing: 'border-box',
                                margin: theme.spacing(1, 0, 1, 0),
                            }}
                        >
                            <Container
                                style={{
                                    ...typographyStyle(
                                        theme.typography.caption,
                                    ),
                                }}
                            >
                                Created Date:
                            </Container>
                            <Container
                                style={{
                                    fontWeight: 700,
                                    marginLeft: theme.spacing(1),
                                }}
                            >
                                {toMMDDYY(new Date(workorder.created_date))}
                            </Container>
                        </Container>
                    )}
                </Container>

                {/* Right Column image */}
                <Container flex={1} alignItems="center" justifyContent="center">
                    {attachment ? (
                        <Attachment
                            attachment={attachment.file}
                            uploaded={attachment.uploaded}
                            theme={theme}
                            size={130}
                        />
                    ) : !hideCameraButton ? (
                        <Container>
                            <label htmlFor={`icon-button-file_${workorder.id}`}>
                                <Container
                                    style={{
                                        border: `1px solid ${theme.palette.grey[900]}`,
                                        width: 100,
                                        height: 100,
                                        borderRadius: theme.shape.borderRadius,
                                        marginLeft: theme.spacing(2),
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <AddAPhoto fontSize="large" />
                                </Container>
                            </label>
                            <input
                                accept="image/png, image/jpeg, image/jpg"
                                id={`icon-button-file_${workorder.id}`}
                                type="file"
                                style={{ display: 'none' }}
                                onChange={async (e) => {
                                    if (createMessage) {
                                        e.stopPropagation()
                                        e.preventDefault()

                                        if (
                                            e.target.files &&
                                            e.target.files[0]
                                        ) {
                                            const message = new FormData()
                                            message.append(
                                                'channel',
                                                String(workorder.channel),
                                            )
                                            message.append(
                                                'attachments_len',
                                                String(e.target.files.length),
                                            )
                                            for (
                                                let i = 0;
                                                i <= e.target.files.length;
                                                i++
                                            ) {
                                                const attachmentNum = `attachment${i}`
                                                message.append(
                                                    attachmentNum,
                                                    e.target.files[i],
                                                )
                                            }

                                            createMessage(message).then(
                                                (res) => {
                                                    updateWorkorderState({
                                                        ...workorder,
                                                        attachment_list:
                                                            res.data
                                                                .attachments,
                                                    })
                                                },
                                            )
                                        }
                                    }
                                }}
                            />
                        </Container>
                    ) : null}
                </Container>
            </Container>
            <Container direction="column" style={{ width: '100%' }}>
                {workorder.sub_vendor_name != null &&
                user?.active_workspace?.company_type === VENDOR ? (
                    <Container
                        style={{
                            alignItems: 'center',
                            padding: theme.spacing(0, 1, 0.5, 1),
                            boxSizing: 'border-box',
                        }}
                    >
                        <DoneAll
                            color="action"
                            fontSize="small"
                            style={{ marginRight: theme.spacing(0.75) }}
                        />
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                            }}
                        >
                            {workorder.sub_vendor_name}
                        </span>
                    </Container>
                ) : null}
                <Container style={{ height: '50px', paddingRight: 52 }}>
                    <Container
                        flex={1}
                        style={{
                            padding: theme.spacing(1),
                            boxSizing: 'border-box',
                            alignItems: 'center',
                        }}
                    >
                        <Container
                            flex={1}
                            style={{
                                height: '35px',
                                alignSelf: 'center',
                                marginRight: theme.spacing(1),
                            }}
                        >
                            <WorkorderStatusBadge
                                workorder={workorder}
                                theme={theme}
                            />
                        </Container>
                        <Container flex={1}>
                            {!hideMessageButtons && (
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        openMessageDrawer()
                                    }}
                                >
                                    <Badge
                                        badgeContent={workorder.message_count}
                                        color={
                                            (workorder.unread_count ?? 0) > 0
                                                ? 'primary'
                                                : 'secondary'
                                        }
                                    >
                                        <ChatBubbleOutline />
                                    </Badge>
                                </IconButton>
                            )}
                        </Container>
                        <Container flex={1}>
                            {!hideMessageButtons && props.canSeeUnitNotes && (
                                <Tooltip title="Unit Notes">
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            openUnitNotes()
                                        }}
                                        size="small"
                                    >
                                        <Badge
                                            badgeContent={
                                                workorder.unit_notes_msg_count
                                            }
                                            color={
                                                (workorder.unread_unit_notes ??
                                                    0) > 0
                                                    ? 'secondary'
                                                    : 'primary'
                                            }
                                        >
                                            <UnitNotesIcon />
                                        </Badge>
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Container>
                        <Container style={{ flex: 1 }}>
                            {workorder.completion_video?.uploaded &&
                                onClickVideo && (
                                    <Tooltip title="Watch completion video">
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                if (
                                                    workorder.completion_video
                                                ) {
                                                    onClickVideo(
                                                        workorder
                                                            .completion_video
                                                            .id,
                                                    )
                                                }
                                            }}
                                            size="small"
                                        >
                                            <PlayCircleOutline
                                                style={{
                                                    color:
                                                        theme.palette.primary
                                                            .main,
                                                }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                )}
                        </Container>
                        <Container style={{ flex: 1 }}>
                            <Tooltip title={workorderTooltip}>
                                {workorderIcon}
                            </Tooltip>
                        </Container>
                        <Container style={{ flex: 1 }}>{editButton}</Container>
                        <Container style={{ flex: 1 }}>
                            {workorder.info && (
                                <WorkorderInfoPopover
                                    workorder={workorder}
                                    theme={theme}
                                />
                            )}
                        </Container>

                        <Container style={{ flex: 1 }}>
                            {priorityIcon}
                        </Container>
                    </Container>
                    <Container flex={1}>
                        {!earlyAccessRequest && (
                            <WorkorderTransitionButtons
                                workorder={workorder}
                                user={user}
                                handleTransition={(e, workorder, status) => {
                                    e.stopPropagation()
                                    transitionWorkorder(workorder, status, {
                                        all_jobsites: true,
                                    }).then(() => {
                                        toast.success('Success')
                                        if (getRFPDetail) {
                                            getRFPDetail()
                                        }
                                    })
                                }}
                                disabled={cantStartWorkorder}
                                canRequestEarlyAccess={
                                    user?.active_workspace?.company_type ===
                                    VENDOR
                                }
                                onCreateWorkorderEarlyAccessRequest={
                                    onCreateWorkorderEarlyAccessRequest
                                }
                                activeWorkspace={user?.active_workspace}
                                onClickGoBack={onClickGoBack}
                            />
                        )}
                        {earlyAccessRequest && onHandleEarlyAccessRequest && (
                            <EarlyAccessRequestTransitionButtons
                                earlyAccessRequest={earlyAccessRequest}
                                onHandleEarlyAccessRequest={
                                    onHandleEarlyAccessRequest
                                }
                            />
                        )}
                        {showResetButton && (
                            <Button
                                size="small"
                                color="secondary"
                                variant="outlined"
                                style={{ margin: theme.spacing(1, 0, 1, 1) }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    transitionWorkorder(
                                        workorder,
                                        WorkorderStatus.ASSIGNED,
                                    )
                                }}
                            >
                                Reset
                            </Button>
                        )}
                    </Container>
                </Container>
            </Container>
            <span
                style={{
                    position: 'absolute',
                    right: theme.spacing(1),
                    bottom: theme.spacing(0.75),
                    ...typographyStyle(theme.typography.caption),
                    fontWeight: 700,
                    color: theme.palette.grey[600],
                    lineHeight: '16px',
                    pointerEvents: 'none',
                }}
            >
                #{workorder.id}
            </span>
            {selectionMode && (
                <button
                    type="button"
                    aria-pressed={isSelectedWorkorder}
                    aria-label={`${
                        isSelectedWorkorder ? 'Deselect' : 'Select'
                    } work order ${workorder.unit_name ?? workorder.id}`}
                    onClick={() => onClickWorkorder(workorder)}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 2,
                        cursor: 'pointer',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        background: 'transparent',
                        borderRadius:
                            typeof theme.shape.borderRadius === 'number'
                                ? theme.shape.borderRadius
                                : `${theme.shape.borderRadius}`,
                    }}
                />
            )}
        </Container>
    )
}
